import type { AppDispatch } from "../../../common/state/store";
import { getConnection } from "./adbConnection";
import { shellCmd } from "./adbShell";
import type { IInstalledPackage } from "../state/deviceInstallerSlice";
import {
  setInstalledPackages,
  removeInstalledPackage,
  setPackageScanStatus,
} from "../state/deviceInstallerSlice";

/** Parse `pm list packages -3` output (with or without --show-versioncode) */
export function parsePackageList(output: string): Record<string, IInstalledPackage> {
  const packages: Record<string, IInstalledPackage> = {};
  for (const line of output.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("package:")) continue;

    const withoutPrefix = trimmed.slice("package:".length);
    const vcMatch = withoutPrefix.match(/^(\S+)\s+versionCode:(\d+)/);
    if (vcMatch) {
      packages[vcMatch[1]] = {
        packageName: vcMatch[1],
        versionCode: parseInt(vcMatch[2], 10),
      };
    } else {
      const pkgName = withoutPrefix.trim();
      if (pkgName) {
        packages[pkgName] = { packageName: pkgName, versionCode: null };
      }
    }
  }
  return packages;
}

/** Scan installed third-party packages on the connected device */
export async function scanInstalledPackages(
  dispatch: AppDispatch,
): Promise<Record<string, IInstalledPackage>> {
  const adb = getConnection();
  if (!adb) throw new Error("No ADB connection");

  dispatch(setPackageScanStatus("scanning"));
  try {
    let output: string;
    try {
      output = await shellCmd(adb, ["pm", "list", "packages", "-3", "--show-versioncode"]);
    } catch {
      output = "";
    }
    let packages = parsePackageList(output);

    // Fallback without versionCode if nothing was parsed
    if (Object.keys(packages).length === 0) {
      try {
        output = await shellCmd(adb, ["pm", "list", "packages", "-3"]);
        packages = parsePackageList(output);
      } catch {
        // Give up
      }
    }

    dispatch(setInstalledPackages(packages));
    dispatch(setPackageScanStatus("done"));
    return packages;
  } catch {
    dispatch(setPackageScanStatus("error"));
    return {};
  }
}

/** Uninstall a package from the device */
export async function uninstallPackage(
  packageName: string,
  dispatch: AppDispatch,
): Promise<boolean> {
  const adb = getConnection();
  if (!adb) return false;

  try {
    const output = await shellCmd(adb, ["pm", "uninstall", packageName]);
    if (output.includes("Success")) {
      dispatch(removeInstalledPackage(packageName));
      return true;
    }
    return false;
  } catch {
    return false;
  }
}
